/* Tracking Event Editor Main Controller
 *
 */

import angular from "angular";

import ngSanitize from "angular-sanitize";
import ngAnimate from "angular-animate";
import ngAria from "angular-aria";

import uiRouter from "@uirouter/angularjs";
import uiBootstrap from "angular-ui-bootstrap";
import uiRouterPreviousStateModule from "tbClient/services/PreviousStateService";

import kendoDirectives from "../../shims/kendoDirectives";

//noinspection NpmUsedModulesInstalled
import Assert from "tbAssert";
import DependencyListBuilder from "../../utility/DependencyListBuilder";

import TrackingCalendarViewModuleConstants from "../tracking-calendar-view/Constants";
import TrackingTargetViewModuleConstants from "../tracking-targets-view/Constants";
import TrackingEventSearchModuleConstants from "../tracking-event-search/Constants";

import TimeZoneDataServiceModule from "../../services/TimezoneService";
import TimeZoneNameFormatFilterModule from "../../filters/FormatTimeZoneNameFilter";
import AllStatesServiceModule from "../../services/AllStatesService";
import AllTrackingStatusesServiceModule from "../../services/AllTrackingStatusesService";
import AllEventTypesServiceModule from "../../services/AllEventTypesService";
import TrackingCalendarDataServiceModule from "../../services/TrackingCalendarDataService";
import UserInfoServiceModule from "../../services/UserInfoService";
import StatusLabelComponentModule from "../../directives/StatusLabel";
import OfficeTypeLabelTypeFilterModule from "../../filters/OfficeTypeLabelTypeFilter";
import CurrentCandidaciesFilterModule from "./CurrentCandidaciesFilter";
import TrackingEventWorkflowServiceModule from "./event-transition/TrackingEventWorkflowService";
import AlertTypeFilterModule from "../../filters/AlertTypeFilter";
import TrackingTargetFactoryModule from "../../models/factories/TrackingTargetFactory";
import UserMessagesServiceModule from "../../services/UserMessagesService";
import CalendarGlobalViewServiceModule from "../../services/CalendarGlobalViewService";

import EventTrackersEditorDialogController from "./EventTrackersEditorDialogController";
import EventAttendeesEditorDialogController from "./EventAttendeesEditorDialogController";
import EventUnsavedChangesWarningDialogController from "./event-unsaved-changes-warning-dialog/EventUnsavedChangesWarningDialogController";

import ConfirmDeleteDialogController from "../confirm-delete-dialog/ConfirmDeleteDialogController";
import ConfirmDeleteDialogResult from "../confirm-delete-dialog/ConfirmDeleteDialogResult";

import TrackingEventWorkflowCurrentStatus from "./event-transition/TrackingEventWorkflowCurrentStatus";
import WorkflowCancellationError from "../../models/errors/WorkflowCancellationError";
import WorkflowError from "../../models/errors/WorkflowError";

import TrackingCalendarDataOperationError from "../../models/errors/TrackingCalendarDataOperationError";
import TrackingCalendarDataOperationCancelledError from "../../models/errors/TrackingCalendarDataOperationCancelledError";
import TrackingEventEditorFlowError from "./TrackingEventEditorFlowError";
import TrackingEventEditorFlowCancelledError from "./TrackingEventEditorFlowCancelledError";

const eventEditorRootElementId = "tbEventEditorRoot";

const editorModalDialogLargeSize = "lg";
const editorModalDialogWindowClass = "tbIn";
const editorModalDialogBackdropClass = "tbIn";

import TrackingEvent from "../../models/TrackingEvent";
import EventType from "../../models/EventType";
import Tracker from "../../models/Tracker";
import Subscription from "../../models/Subscription";

import TbAppConstants from "../../config/tbAppConstants";

import CachedTrackingEventReport from "./CachedTrackingEventReport";
import MessageQueue from "../../models/MessageQueue";

import { GlobalViewNames } from "tbClient/services/WellKnownGlobalViews";

const mScopeSvc = new WeakMap();
const mSanitizeSvc = new WeakMap();
const mHttpSvc = new WeakMap();
const mTimeoutSvc = new WeakMap();
const mFilterSvc = new WeakMap();
const mDocumentSvc = new WeakMap();
const mWindowSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mAnchorScrollSvc = new WeakMap();

const mUIRouterStateSvc = new WeakMap();
const mUIRouterStateParams = new WeakMap();
const mUIRouterPreviousStateSvc = new WeakMap();
const mUIBModalSvc = new WeakMap();

const mTimeZoneDataSvc = new WeakMap();
const mTimeZoneNameFormatFilter = new WeakMap();
const mAllStatesSvc = new WeakMap();
const mAllTrackingStatusesSvc = new WeakMap();
const mAllEventTypesSvc = new WeakMap();
const mTrackingCalendarDataSvc = new WeakMap();
const mUserInfoSvc = new WeakMap();
const mTrackingTargetFactorySvc = new WeakMap();
const mUserMessagesSvc = new WeakMap();
const mGlobalViewSvc = new WeakMap();

const mTrackingEventWorkflowSvc = new WeakMap();

const prevStateInitTable = new Map([
    [TrackingCalendarViewModuleConstants.rootStateName, "prevCalendar"],
    [TrackingEventSearchModuleConstants.rootStateName, "prevSearch"],
    [TrackingTargetViewModuleConstants.rootStateName, "prevTrackingTarget"]
]);

const defaultPrevStateInitTableEntry = TrackingCalendarViewModuleConstants.rootStateName;

const prevStateHandlerTable = new Map([
    ["prevSearch", { memoName: TrackingEventSearchModuleConstants.prevStateLabel, buttonText: "Return to Search Results" }],
    ["prevCalendar", { memoName: TrackingCalendarViewModuleConstants.prevStateLabel, buttonText: "Return to Calendar" }],
    ["prevTrackingTarget", {memoName: TrackingTargetViewModuleConstants.prevStateLabel, buttonText: "Return to Tracking Target" }]
]);

const defaultReturnToPreviousButtonText = "Go to Calendar";

const defaultMessageViewTime = 4000;
const defaultErrorMessageViewTime = defaultMessageViewTime;
const defaultSuccessMessageViewTime = 1000;
const defaultMessageType = "info";
const errorMessageType = "danger";
const successMessageType = "success";

const defaultLoadingMessageType = "primary";
//const infoLoadingMessageType = "info";
const successLoadingMessageType = successMessageType;
const errorLoadingMessageType = errorMessageType;


//FIXME: This really belongs in the All Tracking Statuses service.
const proposedTrackingStatusName = "Proposed";

//Private Members
const mInLoadingState = new WeakMap();
const mLoadingMessage = new WeakMap();
const mLoadingMessageType = new WeakMap();

const mStatusMessageQueue = new WeakMap();
const mHasStatusMessages = new WeakMap();

const mUnmodifiedTrackingEvent = new WeakMap();
const mTrackingEventIsModified = new WeakMap();

const mCurrentTrackingEventId = new WeakMap();
const mCurrentTrackingEvent = new WeakMap();
const mTrackingEventReport = new WeakMap();
const mTrackingEventReportIsDownloaded = new WeakMap();
const mPrevStateType = new WeakMap();
const mAvailableTimeZones = new WeakMap();
const mAvailableStates = new WeakMap();

const mEventStartFormatted = new WeakMap();
const mEventStartTZ = new WeakMap();
const mEventStartUTCFormatted = new WeakMap();
const mEventEndFormatted = new WeakMap();
const mEventEndTZ = new WeakMap();
const mEventEndUTCFormatted = new WeakMap();

const mWorkflowDropdownIsOpen = new WeakMap();
const mEventDisplayNameEditorVisible = new WeakMap();
const mEventDisplayNamePreEditValue = new WeakMap();
const mShowEventSubscribers = new WeakMap();
const mHideOrShowEventSubscribersMessage = new WeakMap();
const mEventSubscribers = new WeakMap();
const mDisplayTrackingReport = new WeakMap();
const mShowEditTrackingReportOOBActions = new WeakMap();
const mShowDeleteTrackingEventActions = new WeakMap();

const mEventTypeEditingMode = new WeakMap();
const mSelectedEventTypes = new WeakMap();
const mEventTypesList = new WeakMap();
const mEventTypesSelectorOptions = new WeakMap();

const mDateTimePickerModelOptions = new WeakMap();
const mStartDateTimePickerOptions = new WeakMap();
const mEndDateTimePickerOptions = new WeakMap();

function getDocumentTitleFromEvent(eventData) {
    if ((eventData == null) ||
        (eventData.getEventDisplayName == null))
    {
        return "TrackerBase: Event";
    }
    else {
        let evtName = eventData.getEventDisplayName();
        if (evtName.length > 25) {
            evtName = evtName.substr(0, 22) + "...";
        }
        return `TrackerBase Event: ${evtName} (${eventData.eventStart.format("YYYY/M/D")})`;
    }
}

function prepareAvailableTimeZoneData(allZones, formatTZNameFn) {

    let preparedZones = allZones.map(function(tzName) {
        let tzDisplayName = formatTZNameFn(tzName);

        return {
            zoneDisplayName: tzDisplayName,
            zoneId: tzName
        };
    });

    return preparedZones;
}

function prepareAvailableStates(statesData) {
    return statesData.map((stateModel) => {
        return {
            stateAbbreviation: stateModel.shortName,
            stateName: stateModel.fullName
        };
    });
}

class TrackingEventEditorController {
    constructor($scope,
                $sanitize,
                $http,
                $timeout,
                $filter,
                $document,
                $window,
                $anchorScroll,
                $log,
                $state,
                $transition$,
                $previousState,
                $uibModal,
                TimeZone,
                tbFormatTZNameFilter,
                AllStatesService,
                AllTrackingStatuses,
                AllEventTypesService,
                TrackingCalendarData,
                UserInfoService,
                TrackingEventWorkflowService,
                TrackingTargetFactory,
                UserMessages,
                CalendarGlobalViewService)
    {
        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert($sanitize != null, "Assertion Failure: Missing Ng-Sanitize Service Dependency");
        Assert.StrictAssert($http != null, "Assertion Failure: Missing Http Service Dependency");
        Assert.StrictAssert($timeout != null, "Assertion Failure: Missing Timeout Service Dependency");
        Assert.StrictAssert($filter != null, "Assertion Failure: Missing Filter Service Dependency");
        Assert.StrictAssert($document != null, "Assertion Failure: Missing Document Service Dependency");
        Assert.StrictAssert($window != null, "Assertion Failure: Missing Document Service Dependency");
        Assert.StrictAssert($anchorScroll != null, "Assertion Failure: Missing Anchor Scroll Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");

        Assert.StrictAssert($state != null, "Assertion Failure: Missing UI Router State Service Dependency");
        Assert.StrictAssert($transition$ != null, "Assertion Failure: Missing UI Router Transition Object Dependency");
        Assert.StrictAssert($previousState != null, "Assertion Failure: Missing UI Router Previous State Service Dependency");
        Assert.StrictAssert($uibModal != null, "Assertion Failure: Missing UIB Modal Service Dependency");

        Assert.StrictAssert(TimeZone != null, "Assertion Failure: Missing Time Zone Service Dependency");
        Assert.StrictAssert(tbFormatTZNameFilter != null, "Assertion Failure: Missing Time Zone Name Format Filter Dependency");
        Assert.StrictAssert(AllStatesService != null, "Assertion Failure: Missing All States Service Dependency");
        Assert.StrictAssert(AllTrackingStatuses != null, "Assertion Failure: Missing All Tracking Statuses Service Dependency");
        Assert.StrictAssert(AllEventTypesService != null, "Assertion Failure: Missing All Event Types Service Dependency");
        Assert.StrictAssert(TrackingCalendarData != null, "Assertion Failure: Missing Tracking Calendar Data Service Dependency");
        Assert.StrictAssert(UserInfoService != null, "Assertion Failure: Missing User Info Service Dependency");
        Assert.StrictAssert(TrackingEventWorkflowService != null, "Assertion Failure: Missing Event Workflow Pre-Check Service Dependency");
        Assert.StrictAssert(TrackingTargetFactory != null, "Assertion Failure: Missing Tracking Target Factory Service Dependency");
        Assert.StrictAssert(UserMessages != null, "Assertion Failure: Missing User Messages Service Dependency");
        Assert.StrictAssert(CalendarGlobalViewService != null, "Assertion Failure: Missing Global View Service Dependency");

        mScopeSvc.set(this, $scope);
        mSanitizeSvc.set(this, $sanitize);
        mHttpSvc.set(this, $http);
        mTimeoutSvc.set(this, $timeout);
        mFilterSvc.set(this, $filter);
        mDocumentSvc.set(this, $document);
        mWindowSvc.set(this, $window);
        mAnchorScrollSvc.set(this, $anchorScroll);
        mLogSvc.set(this, $log);

        mStatusMessageQueue.set(this, new MessageQueue(defaultMessageType, defaultMessageViewTime));
        mHasStatusMessages.set(this, false);

        mUIRouterStateSvc.set(this, $state);
        mUIRouterStateParams.set(this, $transition$.params());
        mUIRouterPreviousStateSvc.set(this, $previousState);
        mUIBModalSvc.set(this, $uibModal);

        mUserInfoSvc.set(this, UserInfoService);

        mTimeZoneDataSvc.set(this, TimeZone);
        mTimeZoneNameFormatFilter.set(this, tbFormatTZNameFilter);
        mAllStatesSvc.set(this, AllStatesService);
        mAllTrackingStatusesSvc.set(this, AllTrackingStatuses);
        mAllEventTypesSvc.set(this, AllEventTypesService);
        mTrackingCalendarDataSvc.set(this, TrackingCalendarData);
        mTrackingEventWorkflowSvc.set(this, TrackingEventWorkflowService);
        mTrackingTargetFactorySvc.set(this, TrackingTargetFactory);
        mUserMessagesSvc.set(this, UserMessages);
        mGlobalViewSvc.set(this, CalendarGlobalViewService);

        mCurrentTrackingEventId.set(this, null);
        mCurrentTrackingEvent.set(this, null);
        mUnmodifiedTrackingEvent.set(this, null);
        mTrackingEventReport.set(this, null);

        mEventDisplayNameEditorVisible.set(this, false);

        mAvailableTimeZones.set(this, []);
        mAvailableStates.set(this, []);

        mShowEventSubscribers.set(this, false);
        mHideOrShowEventSubscribersMessage.set(this, "Show");
        mEventSubscribers.set(this, []);

        mDisplayTrackingReport.set(this, false);

        mTrackingEventReport.set(this, new CachedTrackingEventReport());
        mTrackingEventReportIsDownloaded.set(this, false);

        mShowEditTrackingReportOOBActions.set(this, false);
        mShowDeleteTrackingEventActions.set(this, false);

        mEventTypeEditingMode.set(this, false);
        mSelectedEventTypes.set(this, []);
        mEventTypesList.set(this, []);

        mEventTypesSelectorOptions.set(this, {
            dataSource: mEventTypesList.get(this),
            dataTextField: "eventTypeName",
            dataValueField: "eventTypeId"
        });

        mDateTimePickerModelOptions.set(this, {
            updateOn: "blur"
        });

        mStartDateTimePickerOptions.set(this, {
            format: "M/d/yyyy h:mm tt",
            timeFormat: "h:mm tt",
            interval: 15
        });

        mEndDateTimePickerOptions.set(this, {
            format: "M/d/yyyy h:mm tt",
            timeFormat: "h:mm tt",
            interval: 15
        });

        mEventStartFormatted.set(this, null);
        mEventStartTZ.set(this, null);
        mEventStartUTCFormatted.set(this, null);
        mEventEndFormatted.set(this, null);
        mEventEndTZ.set(this, null);
        mEventEndUTCFormatted.set(this, null);

        let eventIdParam = parseInt(mUIRouterStateParams.get(this).eventId);

        if (Number.isInteger(eventIdParam)) {
            mCurrentTrackingEventId.set(this, eventIdParam);
        }
        else {
            mLogSvc.get(this).error(UserMessages.getUserMessage({msgId: 0x1A00}));
            mUIRouterStateSvc.get(this).go(TrackingCalendarViewModuleConstants.rootStateName);
            return;
        }

        mPrevStateType.set(this, null);

        let prevState = $previousState.get();
        if ((prevState != null) &&
            (prevState.state != null))
        {
            let prevStateType = prevStateInitTable.get(prevState.state.name);
            if (prevStateType == null) {
                prevStateType = prevStateInitTable.get(defaultPrevStateInitTableEntry);
            }

            let prevMemoName = prevStateHandlerTable.get(prevStateType).memoName;
            $previousState.memo(prevMemoName);
            mPrevStateType.set(this, prevStateType);
        }

        mWorkflowDropdownIsOpen.set(this, false);

        // Destroy kendo widgets when the controller is destroyed.
        mScopeSvc.get(this).$on(
            "$destroy",
            () => {
                //noinspection JSUnresolvedFunction
                let eventViewRootElement = angular.element(`#${eventEditorRootElementId}`);
                kendo.destroy(eventViewRootElement);
            }
        );

    }


    //noinspection JSUnusedGlobalSymbols
    $onInit() {

        const scopeSvc = mScopeSvc.get(this);
        const logSvc = mLogSvc.get(this);
        const timeoutSvc = mTimeoutSvc.get(this);
        logSvc.debug("In Tracking Event Editor Controller onInit!");

        const mainNavBarElementIdSelector = `#${TbAppConstants.mainNavBarElementId}`;
        mAnchorScrollSvc.get(this).yOffset = angular.element(mainNavBarElementIdSelector);

        mEventDisplayNamePreEditValue.set(this, null);

        let initEventEditorPipeline = [];
        const initEventEditorFlowName = "InitializeEventEditorFlow";

        logSvc.debug(`Building ${initEventEditorFlowName}`);

        initEventEditorPipeline.push(this._addActivateLoadingStateFlowStep({}));
        initEventEditorPipeline.push(this._addSetLoadingMessageFlowStep(0x1001));

        this._buildInitEventEditorPipeline_loadTrackingEvent({
            initEventEditorPipeline: initEventEditorPipeline,
            baseFlowName: initEventEditorFlowName
        });

        this._buildInitEventEditorPipeline_loadTimeZoneData({
            initEventEditorPipeline: initEventEditorPipeline,
            baseFlowName: initEventEditorFlowName
        });

        this._buildInitEventEditorPipeline_loadStateData({
            initEventEditorPipeline: initEventEditorPipeline,
            baseFlowName: initEventEditorFlowName
        });

        this._buildInitEventEditorPipeline_loadEventTypes({
            initEventEditorPipeline: initEventEditorPipeline,
            baseFlowName: initEventEditorFlowName
        });

        initEventEditorPipeline.push((flowPromise) => flowPromise.then(
            () => this._initEventEditor_initUiAfterEventEditorDataLoaded()
        ));

        // Add watch for the tracking event report downloaded flag
        initEventEditorPipeline.push((flowPromise) => flowPromise.then(() => {
            scopeSvc.$watch(
                () => mTrackingEventReport.get(this).downloaded,
                (newVal, oldVal) => {
                    //noinspection JSValidateTypes
                    if (newVal !== oldVal) {
                        mTrackingEventReportIsDownloaded.set(this, newVal);
                        logSvc.debug(`Updated Tracking Event Report Is Downloaded to ${newVal}`);
                    }
                }
            );
        }));

        // Add watch for event data changes.
        initEventEditorPipeline.push((flowPromise) => flowPromise.then(() => {
            scopeSvc.$watch(
                () => this._isCurrentEventModified(),
                (newVal, oldVal) => {
                    if (newVal !== oldVal) {
                        mTrackingEventIsModified.set(this, newVal);
                    }
                }
            );
        }));

        // End the pipeline by dropping the loading status and showing a success message
        initEventEditorPipeline.push(this._addDeactivateLoadingStateFlowStep());
        initEventEditorPipeline.push(this._addShowSuccessMessageFlowStep(0x1000));

        initEventEditorPipeline.push(this._addCatchAllErrorHandler({
            flowName: initEventEditorFlowName,
            errorMessageText: 0x1A01
        }));

        // Pipeline error cleanup error handlers.
        // The standard flow error handler is omitted because it fully disposes of any errors it can handle,
        // and we want to handle all errors by sending the user to the tracking calendar.
        initEventEditorPipeline.push((flowPromise) => flowPromise.catch((errorData) =>
            timeoutSvc(() => {
                logSvc.error("Error while loading the tracking event editor.", errorData);
                this.leaveEditor();
            }, defaultErrorMessageViewTime)));

        logSvc.debug(`Running ${initEventEditorFlowName}`);

        let initEventEditorPromise = Promise.resolve("");
        initEventEditorPipeline.forEach((pipelineStepFactory) => {
            initEventEditorPromise = pipelineStepFactory(initEventEditorPromise);
        });

        return initEventEditorPromise;
    }

    _buildInitEventEditorPipeline_loadTrackingEvent({
        initEventEditorPipeline: loadEventPipeline,
        baseFlowName: baseFlowName
    }) {

        const trackingCalendarDataSvc = mTrackingCalendarDataSvc.get(this);
        const timeoutSvc = mTimeoutSvc.get(this);
        const scopeSvc = mScopeSvc.get(this);
        const logSvc = mLogSvc.get(this);

        const loadEventFlowName = `${baseFlowName}_LoadTrackingEventFlow`;
        logSvc.debug(`Building ${loadEventFlowName} flow.`);

        loadEventPipeline.push(this._addActivateLoadingStateFlowStep({}));
        loadEventPipeline.push(this._addSetLoadingMessageFlowStep(0x1018));

        loadEventPipeline.push((flowPromise) => flowPromise.then(() => {
            const currentTrackingEventId = mCurrentTrackingEventId.get(this);
            return trackingCalendarDataSvc.getEvent(currentTrackingEventId);
        }));

        loadEventPipeline.push(this._addWrapCalendarSvcErrorsFlowStep({currentFlowName: loadEventFlowName}));

        loadEventPipeline.push((flowPromise) => flowPromise.then((trackingEvent) =>
                         timeoutSvc(() => {
                             mCurrentTrackingEvent.set(this, trackingEvent);
                             this._storeUneditedTrackingEvent(trackingEvent);
                             scopeSvc.eventCoreForm.$setPristine();
                             scopeSvc.eventCoreForm.$setUntouched();
                             mEventDisplayNameEditorVisible.set(this, false);
                         }, 0)));

        loadEventPipeline.push((flowPromise) => flowPromise.then(() => {
            scopeSvc.$watch(
                () => mCurrentTrackingEvent.get(this),
                (newVal, oldVal) => {
                    //noinspection JSValidateTypes
                    if (oldVal !== newVal) {
                        let docTitle = getDocumentTitleFromEvent(newVal);
                        mDocumentSvc.get(this).attr("title", docTitle);
                    }
                }
            );
        }));



        //no need to return the pipeline, we modified the pipeline directly;
    }

    _buildInitEventEditorPipeline_loadStateData({
        initEventEditorPipeline: loadStateDataPipeline,
        baseFlowName: baseFlowName
    }) {
        const scopeSvc = mScopeSvc.get(this);

        const loadStateDataFlowName = `${baseFlowName}_loadStateDataFlow`;

        loadStateDataPipeline.push(this._addActivateLoadingStateFlowStep({}));
        loadStateDataPipeline.push(this._addSetLoadingMessageFlowStep(0x1015));

        loadStateDataPipeline.push((flowPromise) => flowPromise.then(() =>
            mAllStatesSvc.get(this).GetAllRealStatesAsync()
        ));

        loadStateDataPipeline.push((flowPromise) => flowPromise.then((realStateData) => {
            let preStateLoadFormStatus = scopeSvc.eventCoreForm.$pristine;

            let availableStates = prepareAvailableStates(realStateData);
            mAvailableStates.get(this).length = 0;
            //noinspection JSUnresolvedFunction
            Array.prototype.push.apply(mAvailableStates.get(this), availableStates);

            if (preStateLoadFormStatus === true) {
                scopeSvc.eventCoreForm.$setPristine();
            }
        }));

        loadStateDataPipeline.push((flowPromise) => flowPromise.catch((stateErrorData) => {
            //Pass along already wrapped errors from the pipeline.
            if (stateErrorData instanceof TrackingEventEditorFlowError) { throw stateErrorData; }

            throw new TrackingEventEditorFlowError({
                currentFlowName: loadStateDataFlowName,
                flowErrorMessage: 0x1A13,
                flowErrorData: stateErrorData
            });
        }));

        // Don't need to return the pipeline, we modified the original.
    }

    _buildInitEventEditorPipeline_loadEventTypes({
        initEventEditorPipeline: loadEventTypeDataPipeline,
        baseFlowName: baseFlowName
    }) {

        const loadEventTypeDataFlowName = `${baseFlowName}_loadEventTypeDataFlow`;

        loadEventTypeDataPipeline.push(this._addActivateLoadingStateFlowStep({}));
        loadEventTypeDataPipeline.push(this._addSetLoadingMessageFlowStep(0x1016));

        loadEventTypeDataPipeline.push((flowPromise) => flowPromise.then(
            () => mAllEventTypesSvc.get(this).GetEventTypesAsync()
        ));

        loadEventTypeDataPipeline.push((flowPromise) => flowPromise.then(
            (eventTypeData) => {
                 //noinspection JSUnresolvedFunction
                 Array.prototype.push.apply(
                     this.eventTypesList, eventTypeData);
            }));

        loadEventTypeDataPipeline.push((flowPromise) => flowPromise.catch((eventTypeErrorData) => {
            //Pass along already wrapped errors from the pipeline.
            if (eventTypeErrorData instanceof TrackingEventEditorFlowError) { throw eventTypeErrorData; }

            throw new TrackingEventEditorFlowError({
                currentFlowName: loadEventTypeDataFlowName,
                flowErrorMessage: 0x1A14,
                flowErrorData: eventTypeErrorData
            });
        }));

        // Don't need to return the pipeline, we modified the original.

    }

    _initEventEditor_initUiAfterEventEditorDataLoaded() {

        const scopeSvc = mScopeSvc.get(this);

        if (mCurrentTrackingEvent.get(this) != null) {
            mEventStartFormatted.set(this, this.currentTrackingEvent.eventStart.format("MMMM D, YYYY h:mm A Z z"));
            mEventStartTZ.set(this, this.currentTrackingEvent.eventTimezone);
            mEventStartUTCFormatted.set(this, this.currentTrackingEvent.eventStart.clone().tz("UTC").format("MMMM D, YYYY h:mm A Z z"));
            mEventEndFormatted.set(this, this.currentTrackingEvent.eventEnd.format("MMMM D, YYYY h:mm A Z z"));
            mEventEndTZ.set(this, this.currentTrackingEvent.eventTimezone);
            mEventEndUTCFormatted.set(this, this.currentTrackingEvent.eventEnd.clone().tz("UTC").format("MMMM D, YYYY h:mm A Z z"));
        }

        scopeSvc.$watch(
            () => {
                if (this.currentTrackingEvent != null) {
                    return this.currentTrackingEvent.eventStart;
                }
                else {
                    return this.currentTrackingEvent; // could be undefined or null.
                }
            },
            (newVal, oldVal) => {
                //noinspection JSValidateTypes
                if (newVal !== oldVal) {
                    mEventStartFormatted.set(this, this.currentTrackingEvent.eventStart.format("MMMM D, YYYY h:mm A Z z"));
                    mEventStartTZ.set(this, this.currentTrackingEvent.eventTimezone);
                    mEventStartUTCFormatted.set(this, this.currentTrackingEvent.eventStart.clone().tz("UTC").format("MMMM D, YYYY h:mm A Z z"));
                }
            }
        );

        scopeSvc.$watch(
            () => {
                if (this.currentTrackingEvent != null) {
                    return this.currentTrackingEvent.eventEnd;
                }
                else {
                    return this.currentTrackingEvent; // could be undefined or null.
                }
            },
            (newVal, oldVal) => {
                //noinspection JSValidateTypes
                if (newVal !== oldVal) {
                    mEventEndFormatted.set(this, this.currentTrackingEvent.eventEnd.format("MMMM D, YYYY h:mm A Z z"));
                    mEventEndTZ.set(this, this.currentTrackingEvent.eventTimezone);
                    mEventEndUTCFormatted.set(this, this.currentTrackingEvent.eventEnd.clone().tz("UTC").format("MMMM D, YYYY h:mm A Z z"));
                }
            }
        );

        scopeSvc.$watch(
            () => {
                if (this.currentTrackingEvent != null) {
                    return this.currentTrackingEvent.eventTimezone;
                }
                else {
                    return this.currentTrackingEvent; // could be undefined or null.
                }
            },
            (newVal, oldVal) => {
                //noinspection JSValidateTypes
                if (newVal !== oldVal) {
                    mEventStartFormatted.set(this, this.currentTrackingEvent.eventStart.format("MMMM D, YYYY h:mm A Z z"));
                    mEventStartTZ.set(this, this.currentTrackingEvent.eventTimezone);
                    mEventStartUTCFormatted.set(this, this.currentTrackingEvent.eventStart.clone().tz("UTC").format("MMMM D, YYYY h:mm A Z z"));
                    mEventEndFormatted.set(this, this.currentTrackingEvent.eventEnd.format("MMMM D, YYYY h:mm A Z z"));
                    mEventEndTZ.set(this, this.currentTrackingEvent.eventTimezone);
                    mEventEndUTCFormatted.set(this, this.currentTrackingEvent.eventEnd.clone().tz("UTC").format("MMMM D, YYYY h:mm A Z z"));
                }
            }
        );

        mEventDisplayNameEditorVisible.set(this, false);

        // Initialize the actions menu, then set a $watch so that the actions menu will be updated
        // when the event's status changes.
        
        this._updateActionsMenu();

        scopeSvc.$watch(
            () => ((this.currentTrackingEvent != null) ? this.currentTrackingEvent.statusName : null),
            (newVal, oldVal) => {
                //noinspection JSValidateTypes
                if (newVal !== oldVal) {
                    if (newVal != null)
                    {
                        this._updateActionsMenu();
                    }
                }
            }
        );
    }

    _buildInitEventEditorPipeline_loadTimeZoneData({
        initEventEditorPipeline: loadTimeZonePipeline,
        baseFlowName: baseFlowName
    }) {

        const loadTimeZoneDataFlowName = `${baseFlowName}_loadTimeZoneFlow`;

        loadTimeZonePipeline.push(this._addActivateLoadingStateFlowStep({}));
        loadTimeZonePipeline.push(this._addSetLoadingMessageFlowStep(0x1017));

        loadTimeZonePipeline.push((flowPromise) => flowPromise.then(() =>
            mTimeZoneDataSvc.get(this).getTimeZones({ countryCode: "US" }).$promise
        ));

        loadTimeZonePipeline.push((flowPromise) => flowPromise.then((timeZoneData) => {
            let preTZLoadFormStatus = mScopeSvc.get(this).eventCoreForm.$pristine;

            let availableTimeZones = prepareAvailableTimeZoneData(timeZoneData, mTimeZoneNameFormatFilter.get(this));

            mAvailableTimeZones.get(this).length = 0;
            //noinspection JSUnresolvedFunction
            Array.prototype.push.apply(
                mAvailableTimeZones.get(this), availableTimeZones);

            if (preTZLoadFormStatus === true) {
                mScopeSvc.get(this).eventCoreForm.$setPristine();
            }
        }));

        // Wrap any errors from the time zone data service into a flow error, and throw that.
        loadTimeZonePipeline.push((flowPromise) => flowPromise.catch((timeZoneDataError) => {
            //Pass along already wrapped errors from the pipeline.
            if (timeZoneDataError instanceof TrackingEventEditorFlowError) { throw timeZoneDataError; }

            throw new TrackingEventEditorFlowError({
                currentFlowName: loadTimeZoneDataFlowName,
                flowErrorMessage: 0x1A15,
                flowErrorData: timeZoneDataError
            });
        }));

        // Don't need to return the pipeline, we modified the original.
    }
    
    _updateActionsMenu() {
        const logSvc = mLogSvc.get(this);
        if (this.currentTrackingEvent == null) {
            logSvc.warn("Update Actions Menu: skipped because event not yet ready.");
            return;
        }
        
        let currentTrackingStatus = this.currentTrackingEvent.trackingStatus;

        if (currentTrackingStatus == null) {
            logSvc.warn("Update Actions Menu: skipped because event status not ready.");
            return;
        }

        mShowEditTrackingReportOOBActions.set(
            this,
            ((this.userInfo.hasTrackerRights) &&
                ((currentTrackingStatus.trackingStatusName === proposedTrackingStatusName) ||
                (currentTrackingStatus.isReported === true))
            )
        );

        mShowDeleteTrackingEventActions.set(
            this,
            (
                (
                    (this.userInfo.hasManagementRights) &&
                    (currentTrackingStatus.isReported === false)
                )
                ||
                (this.userInfo.hasAdminRights)
            )
        );
    }

    get internalEventHelpText() {
        return mUserMessagesSvc.get(this).getUserMessage({msgId: 0x1019});
    }

    get externalEventHelpText() {
        return mUserMessagesSvc.get(this).getUserMessage({msgId: 0x1021});
    }

    get externalEventOptionVisible() {
        const currentGlobalView = mGlobalViewSvc.get(this).getCurrentView();
        return currentGlobalView.name !== GlobalViewNames.tracking;
    }

    get showExternalEventOptionWarning() {
        return (!this.externalEventOptionVisible) && this.currentTrackingEvent && this.currentTrackingEvent.isExternal;
    }

    get externalEventOptionWarningText() {
        return mUserMessagesSvc.get(this).getUserMessage({msgId: 0x1500});
    }

    get userInfo() {
        return mUserInfoSvc.get(this);
    }

    get currentTrackingEvent() {
        return mCurrentTrackingEvent.get(this);
    }

    get isLoading() {
        return mInLoadingState.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get loadingMessage() {
        return mLoadingMessage.get(this);
    }

    get loadingMessageType() {
        return mLoadingMessageType.get(this);
    }

    get statusMessages() {
        return mStatusMessageQueue.get(this).messages;
    }

    get hasStatusMessages() {
        return mStatusMessageQueue.get(this).messageCount;
    }

    //noinspection JSUnusedGlobalSymbols
    get workflowDropdownIsOpen() {
        return mWorkflowDropdownIsOpen.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    set workflowDropdownIsOpen(updatedVal) {
        mWorkflowDropdownIsOpen.set(this, updatedVal);
    }

    get eventDisplayNameEditorVisible() {
        return mEventDisplayNameEditorVisible.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get dateTimePickerModelOptions() {
        return mDateTimePickerModelOptions.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get startDateTimePickerOptions() {
        return mStartDateTimePickerOptions.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get endDateTimePickerOptions() {
        return mEndDateTimePickerOptions.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get availableTimeZones() {
        return mAvailableTimeZones.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get eventStartFormatted() {
        return mEventStartFormatted.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get eventStartTZ() {
        return mEventStartTZ.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get eventStartUTCFormatted() {
        return mEventStartUTCFormatted.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get eventEndFormatted() {
        return mEventEndFormatted.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get eventEndTZ() {
        return mEventEndTZ.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get eventEndUTCFormatted() {
        return mEventEndUTCFormatted.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get availableStates() {
        return mAvailableStates.get(this);
    }

    get showEventSubscribers() {
        return mShowEventSubscribers.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get hideOrShowEventSubscribersMessage() {
        return mHideOrShowEventSubscribersMessage.get(this);
    }

    get eventSubscribers() {
        return mEventSubscribers.get(this);
    }

    get trackingEventReport() {
        return mTrackingEventReport.get(this);
    }

    get trackingEventReportIsDownloaded() {
        return mTrackingEventReportIsDownloaded.get(this);
    }

    get displayTrackingReport() {
        return mDisplayTrackingReport.get(this);
    }

    get showEditTrackingReportOOBActions() {
        return mShowEditTrackingReportOOBActions.get(this);
    }

    get showDeleteTrackingEventActions() {
        return mShowDeleteTrackingEventActions.get(this);
    }

    get eventTypeEditingMode() {
        return mEventTypeEditingMode.get(this);
    }

    get selectedEventTypes() {
        return mSelectedEventTypes.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    set selectedEventTypes(updatedVal) {
        mSelectedEventTypes.set(this, updatedVal);
    }

    get eventTypesList() {
        return mEventTypesList.get(this);
    }

    //noinspection JSUnusedGlobalSymbols
    get eventTypesSelectorOptions() {
        return mEventTypesSelectorOptions.get(this);
    }

    get currentEventIsModified() {
        return mTrackingEventIsModified.get(this);
    }

    // Methods

    //noinspection JSUnusedGlobalSymbols
    closeMessage(msgIdx) {
        mStatusMessageQueue.get(this).removeMessageAtIdx(msgIdx);
    }

    _isCurrentEventModified() {
        const logSvc = mLogSvc.get(this);

        const currentTrackingEvent = this.currentTrackingEvent;
        let checkResult = undefined;
        if (currentTrackingEvent != null) {
            const unmodifiedEvent = mUnmodifiedTrackingEvent.get(this);
            if (unmodifiedEvent != null) {
                checkResult = (!currentTrackingEvent.isCoreDataEqual(unmodifiedEvent));
            }
        }
        logSvc.debug(`_isCurrentEventModified: ${checkResult}`);
        return checkResult;
    }

    _storeUneditedTrackingEvent(trackingEvent) {
        if (trackingEvent == null) {
            mUnmodifiedTrackingEvent.set(this, null);
        }

        let unmodifiedClone = JSON.parse(JSON.stringify(trackingEvent));
        mUnmodifiedTrackingEvent.set(this, unmodifiedClone);
    }

    showEventDisplayNameEditor() {
        //noinspection JSUnresolvedFunction
        mEventDisplayNamePreEditValue.set(this, angular.copy(this.currentTrackingEvent.eventName));

        mEventDisplayNameEditorVisible.set(this, true);
    }

    cancelEventDisplayNameEditor() {
        const oldEventName = mEventDisplayNamePreEditValue.get(this);
        if (oldEventName != null) {
            //noinspection JSUnresolvedFunction
            this.currentTrackingEvent.eventName = angular.copy(oldEventName);
            const scopeSvc = mScopeSvc.get(this);

            mTimeoutSvc.get(this)(() => {
                scopeSvc.eventCoreForm.eventNameForm.$setPristine();
                scopeSvc.eventCoreForm.eventNameForm.$setUntouched();
                mEventDisplayNameEditorVisible.set(this, false);
            }, 0);
        }
    }

    getLeaveEditorButtonName() {
        let prevStateType = mPrevStateType.get(this);
        if (prevStateType != null) {
            let prevStateInfo = prevStateHandlerTable.get(prevStateType);
            if (prevStateInfo != null) {
                return prevStateInfo.buttonText;
            }
        }

        return defaultReturnToPreviousButtonText;
    }

    leaveEditor(e) {
        if (e != null) {
            e.preventDefault();
        }

        let prevStateType = mPrevStateType.get(this);
        if (prevStateType != null) {
            let prevStateMemoName = prevStateHandlerTable.get(prevStateType).memoName;
            let prevState = mUIRouterPreviousStateSvc.get(this).get(prevStateMemoName);
            if ((prevState != null) &&
                (prevState.state != null))
            {
                mUIRouterPreviousStateSvc.get(this).go(prevStateMemoName);
                return;
            }
        }

        mUIRouterStateSvc.get(this).go(TrackingCalendarViewModuleConstants.rootStateName);
    }

    //noinspection JSUnusedGlobalSymbols,JSMethodCanBeStatic
    getSubscriptionTypeDisplayName(subType) {
        return Subscription.GetSubscriptionTypeDisplayName(subType);
    }

    _addClearEventSubscribersFlowStep() {
        return (flowPromise) => flowPromise.then((flowData) => {
            this.eventSubscribers.length = 0;
            return flowData;
        });
    }

    _showHideEventSubscribersUI({showUI}) {
        const timeoutSvc = mTimeoutSvc.get(this);
        const userMessagesSvc = mUserMessagesSvc.get(this);
        return ((flowPromise) => flowPromise.then((flowData) => {
            timeoutSvc(() => {
                mShowEventSubscribers.set(this, showUI);
                mHideOrShowEventSubscribersMessage.set(this, userMessagesSvc.getUserMessage({msgId: (showUI ? 0x0003 : 0x004)}));
            }, 0);

            return flowData;
        }));
    }

    toggleEventSubscribersView(e) {
        if (e != null) {
            e.preventDefault();
        }

        const trackingCalendarDataSvc = mTrackingCalendarDataSvc.get(this);
        const logSvc = mLogSvc.get(this);

        const toggleSubscribersFlowName = "toggleEventSubscribersFlow";

        logSvc.debug(`Building ${toggleSubscribersFlowName} event editor flow`);
        let toggleSubscribersInfoViewPipeline = [];

        toggleSubscribersInfoViewPipeline.push(this._addInitialSanityCheckFlowStep({currentFlowName: toggleSubscribersFlowName}));

        if (!this.showEventSubscribers) {
            toggleSubscribersInfoViewPipeline.push((flowPromise) => flowPromise.then(
                (currentTrackingEventData) => trackingCalendarDataSvc.getEventSubscriberInfo(currentTrackingEventData.EventId)
            ));

            toggleSubscribersInfoViewPipeline.push(this._addFlowGenericErrorResponseWrapperHandler({
                flowName: toggleSubscribersFlowName,
                flowMessage: 0x1A04
            }));

            toggleSubscribersInfoViewPipeline.push(this._addClearEventSubscribersFlowStep());

            toggleSubscribersInfoViewPipeline.push((flowPromise) => flowPromise.then((recipientData) => {
                //noinspection JSUnresolvedFunction
                Array.prototype.push.apply(this.eventSubscribers, recipientData);
            }));

            toggleSubscribersInfoViewPipeline.push(this._showHideEventSubscribersUI({showUI: true}));
            toggleSubscribersInfoViewPipeline.push(this._addShowSuccessMessageFlowStep(0x1002));
        }
        else {
            toggleSubscribersInfoViewPipeline.push(this._showHideEventSubscribersUI({showUI: false}));
            toggleSubscribersInfoViewPipeline.push(this._addClearEventSubscribersFlowStep());
        }

        toggleSubscribersInfoViewPipeline.push(this._addFlowErrorHandler());
        toggleSubscribersInfoViewPipeline.push(this._addCatchAllErrorHandler({
            flowName: toggleSubscribersFlowName,
            errorMessageText: 0x1A03
        }));

        logSvc.debug(`Initiating ${toggleSubscribersFlowName} event editor flow`);
        let toggleSubscribersInfoPromise = this._runFlowPipeline({pipelineSteps: toggleSubscribersInfoViewPipeline});

        return toggleSubscribersInfoPromise;
    }

    _addInitialSanityCheckFlowStep({currentFlowName}) {
        return (flowPromise) => flowPromise.then((currentTrackingEventData) => {
            if ((currentTrackingEventData != null) && (currentTrackingEventData instanceof TrackingEvent) && (Number.isInteger(currentTrackingEventData.EventId))) {
                return currentTrackingEventData;
            }
            else {
                throw new TrackingEventEditorFlowError({
                    currentFlowName: currentFlowName,
                    flowErrorMessage: 0x1A02,
                    flowErrorData: currentTrackingEventData
                });
            }
        });
    }

    _addActivateLoadingStateFlowStep({loadingMessageType: msgType = defaultLoadingMessageType}) {
        let timeoutSvc = mTimeoutSvc.get(this);

        const loadingMsgType = msgType != null ? msgType : defaultLoadingMessageType;

        return (flowPromise) => flowPromise.then((promiseVal) => {
            timeoutSvc(() => {
                mLoadingMessageType.set(this, loadingMsgType);
                mInLoadingState.set(this, true);
            }, 0);

            return promiseVal;
        });
    }

    _addDeactivateLoadingStateFlowStep() {
        let timeoutSvc = mTimeoutSvc.get(this);
        return (flowPromise) => flowPromise.then((promiseVal) => {
            timeoutSvc(() => {
                mInLoadingState.set(this, false);
            }, 0);

            return promiseVal;
        });
    }

    _addSetLoadingMessageFlowStep(msgText) {
        let timeoutSvc = mTimeoutSvc.get(this);
        let message = this._processMessageArgs(msgText);
        return (flowPromise) => flowPromise.then((promiseVal) => {
            timeoutSvc(() => {
                mLoadingMessage.set(this, message);
            }, 0);

            return promiseVal;
        });
    }

    _processMessageArgs(msgText) {
        return mUserMessagesSvc.get(this).tryTranslateUserMessage({msg: msgText});
    }

    _addShowSuccessMessageFlowStep(msgText) {
        let timeoutSvc = mTimeoutSvc.get(this);
        let statusMessageQueue = mStatusMessageQueue.get(this);

        const messageText = this._processMessageArgs(msgText);

        return (flowPromise) => flowPromise.then((promiseVal) => {
            timeoutSvc(() => {
                statusMessageQueue.publishMessage(messageText, defaultSuccessMessageViewTime, successMessageType);
            }, 0);

            return promiseVal;
        });
    }

    //noinspection JSUnusedGlobalSymbols
    _addShowDefaultMessageFlowStep(msgText) {
        let timeoutSvc = mTimeoutSvc.get(this);
        let statusMessageQueue = mStatusMessageQueue.get(this);

        const messageText = this._processMessageArgs(msgText);

        return (flowPromise) => flowPromise.then((promiseVal) => {
            timeoutSvc(() => {
                statusMessageQueue.publishMessage(messageText, defaultMessageViewTime, defaultMessageType);
            }, 0);

            return promiseVal;
        });
    }

    // For flow pipelines: adds error handlers to the pipeline that
    // - Deactivates the modal loading state if the error is a flow error or a generic string
    // - Adds the flow error or string's message to the Event Editor's Status Message Queue.
    // - Does not rethrow flow errors or strings.
    // If the error data is neither a flow error nor a string, the handlers throw them down to the pipeline.
    _addFlowErrorHandler() {
        const timeoutSvc = mTimeoutSvc.get(this);
        const logSvc = mLogSvc.get(this);
        const userMessagesSvc = mUserMessagesSvc.get(this);

        const statusMessageQueue = mStatusMessageQueue.get(this);
        return (flowPromise) => flowPromise.catch((errorData) => {
            if (errorData instanceof TrackingEventEditorFlowError ||
                errorData instanceof TrackingEventEditorFlowCancelledError ||
                errorData instanceof String) {

                timeoutSvc(() => {
                    mInLoadingState.set(this, false);
                }, 0);
            }

            throw errorData;
        })
        .catch((errorData) => {
            if (errorData instanceof TrackingEventEditorFlowCancelledError) {
                if (errorData.userCancelledFlow === true) {
                    logSvc.info(`${errorData.message}; cancelled by explicit user request`);
                }
                else {
                    logSvc.info(`${errorData.message}; cancelled by effective user request`);
                }
            }
            else if (errorData instanceof TrackingEventEditorFlowError) {
                timeoutSvc(() => {
                    const errMessage = userMessagesSvc.tryTranslateUserMessage({msg: errorData.errorMessage});
                    statusMessageQueue.publishMessage(errMessage, MessageQueue.timeoutOverrideValue, errorMessageType);
                }, 0);

                logSvc.error(`Error in tracking event editor flow '${errorData.currentFlow}: ${errorData.errorMessage}`);
                if (errorData.errorData != null) {
                    logSvc.debug(errorData.errorData);
                }
            }
            else if ((errorData instanceof String) || (errorData instanceof Number)) {
                timeoutSvc(() => {
                    const errMessage = userMessagesSvc.tryTranslateUserMessage({msg: errorData});
                    statusMessageQueue.publishMessage(errMessage, MessageQueue.timeoutOverrideValue, errorMessageType);
                }, 0);

                logSvc.error(`Error in tracking event editor flow: ${errorData}`);
            }
            else {
                //we don't know how to handle this error, so don't swallow it.
                throw errorData;
            }
        });
    }

    _setLoadingStateErrorMessage(errorMessageText) {
        mLoadingMessageType.set(this, errorLoadingMessageType);
        mInLoadingState.set(this, true);
        mLoadingMessage.set(this, errorMessageText);
    }

    // Adds a catch-all error handler to a flow, which will show
    // the user a provided modal error message and then rethrow the error, to ensure that it is unhandled,
    // which will trigger telemetry and halt further user action in the browser until the page is reloaded.
    _addCatchAllErrorHandler({flowName, errorMessageText}) {
        let timeoutSvc = mTimeoutSvc.get(this);
        const userMessagesSvc = mUserMessagesSvc.get(this);
        let logSvc = mLogSvc.get(this);

        return (flowPromise) => flowPromise
            .catch((errorData) => timeoutSvc(() => {
                const errorMessage = userMessagesSvc.tryTranslateUserMessage({msg: errorMessageText});

                this._setLoadingStateErrorMessage(errorMessage);
                throw errorData;
            }, 0))
            .catch((errorData) => {
                logSvc.error(`During flow ${flowName}, an unexpected error occurred.`, errorData);
                throw errorData;
            });
    }

    // Wrap an operation that returns a generic error response inside a event editor flow error object.
    // This allows for standardized handling later by the default Flow Error Handler.
    // At this point, we do not touch any error response that is an instance of Error, but just rethrow it.
    _addFlowGenericErrorResponseWrapperHandler({flowName, flowMessage}) {
        return (flowPromise) => flowPromise.catch((errorResponse) => {
            if (errorResponse instanceof Error) {
                throw errorResponse;
            }
            else {
                throw new TrackingEventEditorFlowError({
                    currentFlowName: flowName,
                    flowErrorMessage: flowMessage,
                    flowErrorData: errorResponse
                });
            }
        });
    }

    _addFlowDialogErrorResultWrapperHandler({flowName, flowErrorMessage, userCancelledDialogCode}) {
        const userCancelledModalByClickingBackdropResultStatusMessage = "backdrop click";

        return (flowPromise) => flowPromise.catch((dialogDismissResult) => {
            if (dialogDismissResult instanceof Error) {
                throw dialogDismissResult;
            }
            else if (dialogDismissResult === userCancelledDialogCode) {
                throw new TrackingEventEditorFlowCancelledError({
                    flowName: flowName,
                    flowWasUserCancelled: true
                });
            }
            else if (dialogDismissResult === userCancelledModalByClickingBackdropResultStatusMessage) {
                throw new TrackingEventEditorFlowCancelledError({
                    flowName: flowName,
                    flowWasUserCancelled: true
                });
            }
            else {
                throw new TrackingEventEditorFlowError({
                    currentFlowName: flowName,
                    flowErrorMessage: flowErrorMessage,
                    flowErrorData: dialogDismissResult
                });
            }
        });
    }

    _addFlowTransformNullDialogResultIntoFlowCancellation({flowName}) {
        return (flowPromise) => flowPromise.then((dialogResult) => {
            if (dialogResult == null) {
                throw new TrackingEventEditorFlowCancelledError({
                    flowName: flowName,
                    flowWasUserCancelled: false
                });
            }
            //If result isn't null, pass it along to the next stage in the pipeline
            return dialogResult;
        });
    }

    _addWrapCalendarSvcErrorsFlowStep({
        currentFlowName: flowName
    }) {
        return (flowPromise) => flowPromise.catch((errorData) => {
            if (errorData instanceof TrackingCalendarDataOperationCancelledError) {
                throw new TrackingEventEditorFlowCancelledError({
                    flowName: flowName,
                    flowWasUserCancelled: false
                });
            }
            else if (errorData instanceof TrackingCalendarDataOperationError) {
                throw new TrackingEventEditorFlowError({
                    currentFlowName: flowName,
                    flowErrorMessage: errorData.message,
                    flowErrorData: {
                        calendarOperationName: errorData.operationName,
                        calendarOperationErrorData: errorData.errorData
                    }
                });
            }
            else {
                throw errorData;
            }
        });
    }

    _runFlowPipeline({pipelineSteps: flowPipelineSteps}) {
        let flowPromise = Promise.resolve(this.currentTrackingEvent);
        flowPipelineSteps.forEach((pipelineStep) => {
            flowPromise = pipelineStep(flowPromise);
        });

        return flowPromise;
    }

    saveChanges(e) {
        if (e != null) {
            e.preventDefault();
        }

        const saveChangesFlowName = "saveChanges";
        const scopeSvc = mScopeSvc.get(this);
        const editorEventCoreForm = scopeSvc.eventCoreForm;
        const logSvc = mLogSvc.get(this);

        if (editorEventCoreForm.$valid === true) {

            logSvc.debug(`Building ${saveChangesFlowName} event editor flow`);
            let savePipeline = [];
            savePipeline.push(this._addActivateLoadingStateFlowStep({loadingMessageType: defaultLoadingMessageType}));
            savePipeline.push(this._addSetLoadingMessageFlowStep(0x1003));

            savePipeline.push((flowPromise) => flowPromise.then(
                (trackingEventData) => {
                    Assert.StrictAssert(
                        trackingEventData instanceof TrackingEvent,
                        "Tracking Event Editor - Save Changes/Data for update service call not a Tracking Event instance."
                    );
                    return mTrackingCalendarDataSvc.get(this).updateEvent(trackingEventData);
                }
            ));

            savePipeline.push(this._addFlowGenericErrorResponseWrapperHandler({
                flowName: saveChangesFlowName,
                flowMessage: 0x1A05
            }));

            savePipeline.push((flowPromise) => flowPromise.then((updatedEventData) => {
                Assert.StrictAssert(
                    updatedEventData instanceof TrackingEvent,
                    "Tracking Event Editor - Save Changes: Updated event data from update service not a Tracking Event instance."
                );
                mCurrentTrackingEvent.set(this, updatedEventData);
                this._storeUneditedTrackingEvent(updatedEventData);
                editorEventCoreForm.$setPristine();
                editorEventCoreForm.$setUntouched();
                mEventDisplayNameEditorVisible.set(this, false);

                return updatedEventData;
            }));

            savePipeline.push(this._showHideEventSubscribersUI({showUI: false}));
            savePipeline.push(this._addClearEventSubscribersFlowStep());

            savePipeline.push(this._addDeactivateLoadingStateFlowStep());
            savePipeline.push(this._addShowSuccessMessageFlowStep(0x1004));
            savePipeline.push(this._addFlowErrorHandler());
            savePipeline.push(this._addCatchAllErrorHandler({
                flowName: saveChangesFlowName,
                errorMessageText: 0x1A06
            }));

            logSvc.debug(`Initiating ${saveChangesFlowName} event editor flow`);
            let savePipelinePromise = this._runFlowPipeline({pipelineSteps: savePipeline});

            return savePipelinePromise;
        }
        else {
            logSvc.debug(
                "Save changes flow not started: invalid event editor form data not currently valid or an error occurred while checking."
            );
        }
    }

    reloadEvent(e) {
        if (e != null) {
            e.preventDefault();
        }

        const scopeSvc = mScopeSvc.get(this);
        const uibModalSvc = mUIBModalSvc.get(this);
        const timeoutSvc = mTimeoutSvc.get(this);
        const logSvc = mLogSvc.get(this);
        const trackingCalendarDataSvc = mTrackingCalendarDataSvc.get(this);
        const currentTrackingEventId = this.currentTrackingEvent.EventId;

        const reloadFlowName = "reloadEventFlow";

        logSvc.debug(`Building ${reloadFlowName} event editor flow`);
        let reloadFlowPipeline = [];

        reloadFlowPipeline.push(this._addInitialSanityCheckFlowStep({currentFlowName: reloadFlowName}));

        if (this._isCurrentEventModified()) {
            reloadFlowPipeline.push((flowPromise) => flowPromise.then(() => {
                let unsavedChangesWarningDialogParams = {
                    animation: true,
                    template: EventUnsavedChangesWarningDialogController.template,
                    controller: EventUnsavedChangesWarningDialogController ,
                    controllerAs: EventUnsavedChangesWarningDialogController.controllerAs,
                    bindToController: true,
                    backdropClass: editorModalDialogBackdropClass,
                    windowClass: editorModalDialogWindowClass,
                    resolve: {}
                };

                return uibModalSvc.open(unsavedChangesWarningDialogParams).result;
            }));

            reloadFlowPipeline.push(this._addFlowDialogErrorResultWrapperHandler({
                flowName: reloadFlowName,
                flowErrorMessage: 0x1A07,
                userCancelledDialogCode: EventUnsavedChangesWarningDialogController.UserCancelledStatusMessage
            }));
        }

        reloadFlowPipeline.push(this._addActivateLoadingStateFlowStep({}));
        reloadFlowPipeline.push(this._addSetLoadingMessageFlowStep(0x1005));

        reloadFlowPipeline.push(this._showHideEventSubscribersUI({showUI: false}));
        reloadFlowPipeline.push(this._addClearEventSubscribersFlowStep());

        reloadFlowPipeline.push((flowPromise) => flowPromise.then(() =>
            trackingCalendarDataSvc.getEvent(currentTrackingEventId)
        ));

        reloadFlowPipeline.push(this._addWrapCalendarSvcErrorsFlowStep({
            currentFlowName: reloadFlowName
        }));

        reloadFlowPipeline.push((flowPromise) => flowPromise.then((trackingEvent) => {

            Assert.StrictAssert(trackingEvent.EventId === currentTrackingEventId, "Reload: retrieved event with different id than initial.");

            return timeoutSvc(() => {
                mCurrentTrackingEvent.set(this, trackingEvent);
                this._storeUneditedTrackingEvent(trackingEvent);

                scopeSvc.eventCoreForm.$setPristine();
                scopeSvc.eventCoreForm.$setUntouched();
                mEventDisplayNameEditorVisible.set(this, false);
            }, 0);

        }));

        reloadFlowPipeline.push(this._addDeactivateLoadingStateFlowStep());
        reloadFlowPipeline.push(this._addShowSuccessMessageFlowStep(0x1006));

        reloadFlowPipeline.push(this._addFlowErrorHandler());
        reloadFlowPipeline.push(this._addCatchAllErrorHandler({
            flowName: reloadFlowName,
            errorMessageText: 0x1A08
        }));

        logSvc.debug(`Initiating ${reloadFlowName} event editor flow`);
        let reloadFlowPromise = this._runFlowPipeline({
            pipelineSteps: reloadFlowPipeline
        });

        return reloadFlowPromise;
    }

    openTrackersEditor() {
        const logSvc = mLogSvc.get(this);
        const uibModalSvc = mUIBModalSvc.get(this);
        const trackingCalendarDataSvc = mTrackingCalendarDataSvc.get(this);
        const editTrackersFlowName = "editTrackersFlow";

        logSvc.debug(`Building ${editTrackersFlowName} event editor flow pipeline`);
        let editTrackersPipeline = [];
        let currentTrackingEventId = this.currentTrackingEvent.EventId;

        //Add open tracker editor step (factory) to pipeline
        editTrackersPipeline.push((flowPromise) => flowPromise.then((currentTrackingEventData) => {
            let trackersEditorModal = uibModalSvc.open({
                animation: true,
                template: EventTrackersEditorDialogController.template,
                controller: EventTrackersEditorDialogController,
                controllerAs: EventTrackersEditorDialogController.controllerAs,
                bindToController: true,
                backdropClass: editorModalDialogBackdropClass,
                windowClass: editorModalDialogWindowClass,
                resolve: {
                    eventTrackers: () =>
                        currentTrackingEventData.eventTrackers.map(
                            (trk) => Tracker.CopyTracker(trk)
                        )
                }
            });

            return trackersEditorModal.result;
        }));

        editTrackersPipeline.push(this._addFlowDialogErrorResultWrapperHandler({
            flowName: editTrackersFlowName,
            flowErrorMessage: 0x1A09,
            userCancelledDialogCode: EventTrackersEditorDialogController.UserCancelledStatusMessage
        }));

        editTrackersPipeline.push(this._addFlowTransformNullDialogResultIntoFlowCancellation({
            flowName: editTrackersFlowName
        }));

        editTrackersPipeline.push(this._addActivateLoadingStateFlowStep({loadingMessageType: defaultLoadingMessageType}));
        editTrackersPipeline.push(this._addSetLoadingMessageFlowStep(0x1007));

        editTrackersPipeline.push((flowPromise) => flowPromise.then(
            (updatedTrackers) => trackingCalendarDataSvc.updateEventTrackers(currentTrackingEventId, updatedTrackers))
        );

        editTrackersPipeline.push(this._addFlowGenericErrorResponseWrapperHandler({
            flowName: editTrackersFlowName,
            flowMessage: 0x1A05
        }));

        editTrackersPipeline.push((flowPromise) => flowPromise.then((updatedEvent) => {
            this.currentTrackingEvent.eventTrackers = updatedEvent.eventTrackers;
        }));

        editTrackersPipeline.push(this._addDeactivateLoadingStateFlowStep());
        editTrackersPipeline.push(this._addShowSuccessMessageFlowStep(0x1008));

        editTrackersPipeline.push(this._addFlowErrorHandler());
        editTrackersPipeline.push(this._addCatchAllErrorHandler({
            flowName: editTrackersFlowName,
            errorMessageText: 0x1A06
        }));

        logSvc.debug(`Initiating ${editTrackersFlowName} event editor flow`);
        let editTrackersFlowPromise = this._runFlowPipeline({
            pipelineSteps: editTrackersPipeline
        });

        return editTrackersFlowPromise;
    }

    openAttendeesEditor() {
        const logSvc = mLogSvc.get(this);
        const uibModalSvc = mUIBModalSvc.get(this);
        const trackingCalendarDataSvc = mTrackingCalendarDataSvc.get(this);
        const trackingTargetFactorySvc = mTrackingTargetFactorySvc.get(this);
        const editAttendeesFlowName = "editAttendeesFlow";

        logSvc.debug(`Building ${editAttendeesFlowName} event editor flow pipeline`);
        let editAttendeesPipeline = [];
        const currentTrackingEventId = this.currentTrackingEvent.EventId;

        editAttendeesPipeline.push((flowPromise) => flowPromise.then((currentTrackingEventData) => {
            let attendeesEditorModalParams = {
                animation: true,
                template: EventAttendeesEditorDialogController.template,
                controller: EventAttendeesEditorDialogController,
                controllerAs: EventAttendeesEditorDialogController.controllerAs,
                bindToController: true,
                backdropClass: editorModalDialogBackdropClass,
                windowClass: editorModalDialogWindowClass,
                size: editorModalDialogLargeSize,
                resolve: {
                    eventAttendees: () => currentTrackingEventData.eventAttendees.map(
                        (attendee) => trackingTargetFactorySvc.CopyTarget(attendee)
                    )
                }
            };

            return uibModalSvc.open(attendeesEditorModalParams).result;
        }));

        editAttendeesPipeline.push(this._addFlowDialogErrorResultWrapperHandler({
            flowName: editAttendeesFlowName,
            flowErrorMessage: 0x1A0C,
            userCancelledDialogCode: EventAttendeesEditorDialogController.UserCancelledStatusMessage
        }));

        editAttendeesPipeline.push(this._addFlowTransformNullDialogResultIntoFlowCancellation({
            flowName: editAttendeesFlowName
        }));

        editAttendeesPipeline.push(this._addActivateLoadingStateFlowStep({loadingMessageType: defaultLoadingMessageType}));
        editAttendeesPipeline.push(this._addSetLoadingMessageFlowStep(0x1009));

        editAttendeesPipeline.push((flowPromise) => flowPromise.then(
            (updatedAttendees) => trackingCalendarDataSvc.updateEventAttendees(
                currentTrackingEventId,
                updatedAttendees
            ))
        );

        editAttendeesPipeline.push(this._addFlowGenericErrorResponseWrapperHandler({
            flowName: editAttendeesFlowName,
            flowMessage: 0x1A05
        }));

        editAttendeesPipeline.push((flowPromise) => flowPromise.then((updatedEvent) => {
            this.currentTrackingEvent.eventAttendees = updatedEvent.eventAttendees;
        }));

        editAttendeesPipeline.push(this._addDeactivateLoadingStateFlowStep());
        editAttendeesPipeline.push(this._addShowSuccessMessageFlowStep(0x100A));

        editAttendeesPipeline.push(this._addFlowErrorHandler());
        editAttendeesPipeline.push(this._addCatchAllErrorHandler({
            flowName: editAttendeesFlowName,
            errorMessageText: 0x1A06
        }));

        logSvc.debug(`Initiating ${editAttendeesFlowName} event editor flow`);
        let editAttendeesFlowPromise = this._runFlowPipeline({
            pipelineSteps: editAttendeesPipeline
        });

        return editAttendeesFlowPromise;
    }

    _addHandleWorkflowErrorsFlowStep({currentFlowName: flowName, errorMessageForWorkflowErrors: errorFlowMessage}) {
        const logSvc = mLogSvc.get(this);

        return (flowPromise) => flowPromise.catch((errorData) => {
            if (errorData instanceof WorkflowCancellationError) {

                logSvc.debug("Transition workflow result: Workflow cancelled as requested by user");
                throw new TrackingEventEditorFlowCancelledError({
                    flowName: flowName,
                    flowWasUserCancelled: true
                });
            }
            else if (errorData instanceof WorkflowError) {
                //We'll get an error message later in the flow, but this information will help with any future debugging.
                logSvc.debug(`Transition workflow result: Workflow failed during step '${errorData.stepName}': ${errorData.message}`, errorData);

                throw new TrackingEventEditorFlowError({
                    currentFlowName: flowName,
                    flowErrorMessage: errorFlowMessage,
                    flowErrorData: errorData
                });
            }
            else {
                logSvc.error("Transition workflow result: Workflow failed with unexpected error.", errorData);
                throw errorData;
            }
        });
    }

    doWorkflowTransition(e, wtIdx) {
        if (e != null) {
            e.preventDefault();
        }
        const logSvc = mLogSvc.get(this);

        if (this._isCurrentEventModified() === true) {
            logSvc.info("workflow transition aborted: unsaved changes in event form");
            return;
        }

        const timeoutSvc = mTimeoutSvc.get(this);
        const trackingEventWorkflowSvc = mTrackingEventWorkflowSvc.get(this);

        if ((0 <= wtIdx) &&
            (wtIdx < this.currentTrackingEvent.workflowTransitions.length))
        {
            let targetTransition =
                this.currentTrackingEvent.workflowTransitions[wtIdx];

            const transitionFlowName = `${targetTransition.name}Flow`;
            logSvc.debug(`Building ${transitionFlowName} event editor flow`);
            let transitionFlowPipeline = [];

            transitionFlowPipeline.push(this._addInitialSanityCheckFlowStep({
                currentFlowName: transitionFlowName
            }));
            transitionFlowPipeline.push(this._addActivateLoadingStateFlowStep({}));
            transitionFlowPipeline.push(this._addSetLoadingMessageFlowStep(0x100B));

            transitionFlowPipeline.push(this._showHideEventSubscribersUI({showUI: false}));
            transitionFlowPipeline.push(this._addClearEventSubscribersFlowStep());

            transitionFlowPipeline.push((flowPromise) => flowPromise.then(() =>
                trackingEventWorkflowSvc.runTransitionWorkflow({
                    targetEvent: this.currentTrackingEvent,
                    selectedTransition: targetTransition,
                    cachedTrackingEventReport: this.trackingEventReport,
                    dialogOptions: {
                        dialogBackDropClass: editorModalDialogBackdropClass,
                        dialogWindowClass: editorModalDialogWindowClass,
                        dialogSize: editorModalDialogLargeSize
                    }})
            ));

            transitionFlowPipeline.push(this._addHandleWorkflowErrorsFlowStep({
                currentFlowName: transitionFlowName,
                errorMessageForWorkflowErrors: 0x1A0B
              }));

            transitionFlowPipeline.push((flowPromise) => flowPromise.then((workflowCurrentStatus) => {
                mCurrentTrackingEvent.set(this, workflowCurrentStatus.updatedEvent);
                this._storeUneditedTrackingEvent(workflowCurrentStatus.updatedEvent);

                mEventDisplayNameEditorVisible.set(this, false);
                return timeoutSvc(() => {
                    this.trackingEventReport.resetCachedData();
                },0);
            }));

            if (this.displayTrackingReport) {
                transitionFlowPipeline.push((flowPromise) => flowPromise.then(() => this.getTrackingReport()));
            }

            transitionFlowPipeline.push(this._addDeactivateLoadingStateFlowStep());
            transitionFlowPipeline.push(this._addShowSuccessMessageFlowStep(0x100C));

            transitionFlowPipeline.push(this._addFlowErrorHandler());
            transitionFlowPipeline.push(this._addCatchAllErrorHandler({
                 flowName: transitionFlowName,
                 errorMessageText: 0x1A0E
            }));

            logSvc.debug(`Initiating ${transitionFlowName} event editor flow`);
            let transitionFlowPromise = this._runFlowPipeline({
                 pipelineSteps: transitionFlowPipeline
             });

            return transitionFlowPromise;
        }
    }

    _doTrackingReportOOBEditEx() {
        const logSvc = mLogSvc.get(this);
        const timeoutSvc = mTimeoutSvc.get(this);
        const trackingEventWorkflowSvc = mTrackingEventWorkflowSvc.get(this);

        if (this._isCurrentEventModified() === true) {
            logSvc.debug("tracking report out-of-band update aborted: unsaved changes to event.");
            return;
        }

        const trackingReportOobEditFlowName = "trackingReportOobEditFlow";
        logSvc.debug(`Building ${trackingReportOobEditFlowName} event editor flow`);
        let reportOobEditPipeline = [];

        reportOobEditPipeline.push(this._addInitialSanityCheckFlowStep({
            currentFlowName: trackingReportOobEditFlowName
        }));

        reportOobEditPipeline.push(this._addActivateLoadingStateFlowStep({}));
        reportOobEditPipeline.push(this._addSetLoadingMessageFlowStep(0x100D));

        reportOobEditPipeline.push((flowPromise) => flowPromise.then(() =>
            trackingEventWorkflowSvc.doTrackingReportOutOfBandEdit({
                targetEvent: this.currentTrackingEvent,
                trackingEventReport: this.trackingEventReport,
                dialogOptions: {
                    dialogBackDropClass: editorModalDialogBackdropClass,
                    dialogWindowClass: editorModalDialogWindowClass,
                    dialogSize: editorModalDialogLargeSize
                }
            })));

        reportOobEditPipeline.push(this._addHandleWorkflowErrorsFlowStep({
            currentFlowName: trackingReportOobEditFlowName,
            errorMessageForWorkflowErrors: 0x1A0F
        }));

        reportOobEditPipeline.push((flowPromise) => flowPromise.then((workflowStatus) => {
            Assert.StrictAssert(
                workflowStatus.workflowStatus === "OK.EventReportUpdated",
                "Event Editor Controller got unexpected fulfilled workflow result"
            );

            logSvc.debug("Report updated - updated event details:");
            logSvc.debug(workflowStatus.updatedEvent);

            return workflowStatus;
        }));

        reportOobEditPipeline.push(this._addSetLoadingMessageFlowStep(0x100E));
        reportOobEditPipeline.push((flowPromise) => flowPromise.then(
            (workflowStatus) => timeoutSvc(() => {
                Assert.StrictAssert(
                    workflowStatus.workflowStatus === "OK.EventReportUpdated",
                    "Event Editor Controller got unexpected fulfilled workflow result"
                );

                this.trackingEventReport.resetCachedData();
            },0)
        ));

        reportOobEditPipeline.push((flowPromise) => flowPromise.then(() => this.getTrackingReport()));
        reportOobEditPipeline.push(this._addDeactivateLoadingStateFlowStep());
        reportOobEditPipeline.push(this._addShowSuccessMessageFlowStep(0x100F));

        reportOobEditPipeline.push(this._addFlowErrorHandler());
        reportOobEditPipeline.push(this._addCatchAllErrorHandler({
            flowName: trackingReportOobEditFlowName,
            errorMessageText: 0x1A10
        }));

        logSvc.debug(`Initiating ${trackingReportOobEditFlowName} event editor flow`);
        let reportOobEditFlowPromise = this._runFlowPipeline({
            pipelineSteps: reportOobEditPipeline
        });

        return reportOobEditFlowPromise;
    }

    editTrackingReportOOB() {
        this._doTrackingReportOOBEditEx();
    }

    getTrackingReport() {
        return mTrackingEventWorkflowSvc.get(this).getTrackingReport({
            targetEvent: this.currentTrackingEvent,
            cachedTrackingEventReport: this.trackingEventReport
        })
            .then((workflowStatus) => {
                Assert.StrictAssert(
                    workflowStatus instanceof TrackingEventWorkflowCurrentStatus,
                    "Event Editor Controller: Unexpected result type from get tracking report workflow"
                );

                return workflowStatus.updatedReportData;
            })
            .catch((reason) => {
                if (reason instanceof WorkflowError) {
                    mLogSvc.get(this).error(`Error during Get Tracking Report workflow step '${reason.stepName}'`, reason);
                }

                throw reason;
            });
    }

    toggleTrackingReport(e) {
        if (e != null) {
            e.preventDefault();
        }
        const timeoutSvc = mTimeoutSvc.get(this);

        const toggleTrackingReportFlowName = "toggleTrackingReportFlow";
        let toggleTrackingReportFlowPipeline = [];

        if ((!this.displayTrackingReport) &&
            (!this.trackingEventReport.downloaded))
        {
            toggleTrackingReportFlowPipeline.push((flowPromise) => flowPromise.then(() => this.getTrackingReport()));

            toggleTrackingReportFlowPipeline.push(this._addHandleWorkflowErrorsFlowStep({
                currentFlowName: toggleTrackingReportFlowName,
                errorMessageForWorkflowErrors: 0x1A11
            }));

            toggleTrackingReportFlowPipeline.push((flowPromise) => flowPromise.then(() => timeoutSvc(() => {
                mTrackingEventReportIsDownloaded.set(this, this.trackingEventReport.downloaded);
            }, 0)));

            toggleTrackingReportFlowPipeline.push(
                this._addShowSuccessMessageFlowStep(0x1010));
        }

        toggleTrackingReportFlowPipeline.push((flowPromise) => flowPromise.then(timeoutSvc(() => {
            mDisplayTrackingReport.set(this, !this.displayTrackingReport);
        }, 0)));

        toggleTrackingReportFlowPipeline.push(this._addFlowErrorHandler());
        toggleTrackingReportFlowPipeline.push(this._addCatchAllErrorHandler({
            flowName: toggleTrackingReportFlowName,
            errorMessageText: 0x1A12
        }));

        let toggleTrackingReportFlowPromise = this._runFlowPipeline({
            pipelineSteps: toggleTrackingReportFlowPipeline
        });

        return toggleTrackingReportFlowPromise;
    }

    startEventTypeEditFlow() {
        this.selectedEventTypes.length = 0;
        //noinspection JSUnresolvedFunction
        Array.prototype.push.apply(
            this.selectedEventTypes,
            this.currentTrackingEvent.eventTypes.map((et) => EventType.Copy(et))
        );

        mEventTypeEditingMode.set(this, true);
    }

    cancelEventTypeEdit() {
        this.selectedEventTypes.length = 0;
        mEventTypeEditingMode.set(this, false);
    }

    saveSelectedEventTypes() {

        const trackingCalendarDataSvc = mTrackingCalendarDataSvc.get(this);
        const logSvc = mLogSvc.get(this);
        const timeoutSvc = mTimeoutSvc.get(this);
        const eventTypeEditFlowName = "eventTypeEditFlow";

        let updatedEventTypesForEvent = this.selectedEventTypes;

        logSvc.debug(`Building ${eventTypeEditFlowName} event editor flow`);
        let eventTypeFlowPipeline = [];

        eventTypeFlowPipeline.push(this._addInitialSanityCheckFlowStep({
            currentFlowName: eventTypeEditFlowName
        }));

        eventTypeFlowPipeline.push((flowPromise) => flowPromise.then((currentTrackingEventData) => {
            if (updatedEventTypesForEvent == null) {
                throw new TrackingEventEditorFlowCancelledError({
                    flowName: eventTypeEditFlowName,
                    flowWasUserCancelled: false
                });
            }

            return {
                currentEventId: currentTrackingEventData.EventId,
                updatedEventTypes: updatedEventTypesForEvent
            };
        }));

        eventTypeFlowPipeline.push(this._addActivateLoadingStateFlowStep({}));
        eventTypeFlowPipeline.push(this._addSetLoadingMessageFlowStep(0x1011));

        eventTypeFlowPipeline.push((flowPromise) => flowPromise.then((eventTypeUpdateData) => {
            timeoutSvc(() => {
                mEventTypeEditingMode.set(this, false);
            }, 0);

            return eventTypeUpdateData;
        }));

        eventTypeFlowPipeline.push((flowPromise) => flowPromise.then(
            (eventTypeUpdateData) => trackingCalendarDataSvc.updateEventTypes(
                eventTypeUpdateData.currentEventId,
                eventTypeUpdateData.updatedEventTypes)
        ));

        eventTypeFlowPipeline.push(this._addFlowGenericErrorResponseWrapperHandler({
            flowName: eventTypeEditFlowName,
            flowMessage: 0x1A05
        }));

        eventTypeFlowPipeline.push((flowPromise) => flowPromise.then((updatedEvent) => {
            this.currentTrackingEvent.eventTypes = updatedEvent.eventTypes;
        }));

        eventTypeFlowPipeline.push(this._addDeactivateLoadingStateFlowStep());

        eventTypeFlowPipeline.push(this._addShowSuccessMessageFlowStep(0x1012));

        eventTypeFlowPipeline.push(this._addFlowErrorHandler());
        eventTypeFlowPipeline.push(this._addCatchAllErrorHandler({
            flowName: eventTypeEditFlowName,
            errorMessageText: 0x1A06
        }));

        logSvc.debug(`Initiating ${eventTypeEditFlowName} event editor flow`);
        let eventTypeUpdateFlowPromise = this._runFlowPipeline({
            pipelineSteps: eventTypeFlowPipeline
        });

        return eventTypeUpdateFlowPromise;
    }

    deleteTrackingEvent() {
        const uibModalSvc = mUIBModalSvc.get(this);
        const logSvc = mLogSvc.get(this);
        const timeoutSvc = mTimeoutSvc.get(this);
        const trackingCalendarDataSvc = mTrackingCalendarDataSvc.get(this);

        const deleteFlowName = "eventDeleteFlow";

        logSvc.debug(`Building ${deleteFlowName} event editor flow`);
        let deleteEventFlowPipeline = [];

        deleteEventFlowPipeline.push(this._addInitialSanityCheckFlowStep({
            currentFlowName: deleteFlowName
        }));

        deleteEventFlowPipeline.push((flowPromise) => flowPromise.then((currentTrackingEventData) => {
            let deleteEventDialogModal = uibModalSvc.open({
                animation: true,
                template: ConfirmDeleteDialogController.template,
                controller: ConfirmDeleteDialogController,
                controllerAs: ConfirmDeleteDialogController.controllerAs,
                bindToController: true,
                backdropClass: editorModalDialogBackdropClass,
                windowClass: editorModalDialogWindowClass,
                size: editorModalDialogLargeSize,
                resolve: {
                    deleteTarget: () => currentTrackingEventData,
                    deleteTargetDisplayNameGetter: () => function(evt) { return evt.eventName; }
                }
            });

            return deleteEventDialogModal.result;
        }));

        deleteEventFlowPipeline.push(this._addFlowDialogErrorResultWrapperHandler({
            flowName: deleteFlowName,
            flowErrorMessage: 0x1A0A,
            userCancelledDialogCode: ConfirmDeleteDialogController.UserCancelledStatusMessage
        }));

        deleteEventFlowPipeline.push((flowPromise) => flowPromise.then((deleteConfigDialogResult) => {
            if ((deleteConfigDialogResult != null) &&
                (deleteConfigDialogResult.status === ConfirmDeleteDialogController.UserConfirmedStatusMessage)) {

                Assert.StrictAssert(deleteConfigDialogResult instanceof ConfirmDeleteDialogResult, "Unexpected Confirm Delete Dialog Result type");

                return deleteConfigDialogResult.deleteTarget;
            }
            else {
                // A null dialog result or a non-confirm status code should be treated as a cancellation.
                throw new TrackingEventEditorFlowCancelledError({
                    flowName: deleteFlowName,
                    flowWasUserCancelled: false
                });
            }
        }));

        deleteEventFlowPipeline.push((flowPromise) => flowPromise.then((targetEvent) => {
            logSvc.debug(`Delete confirmed. Deleting event with Id #${targetEvent.EventId}`);
            return targetEvent;
        }));

        deleteEventFlowPipeline.push(this._addActivateLoadingStateFlowStep({}));
        deleteEventFlowPipeline.push(this._addSetLoadingMessageFlowStep(0x1013));
        deleteEventFlowPipeline.push((flowPromise) => flowPromise.then(
            (targetEvent) => trackingCalendarDataSvc.deleteEvent(targetEvent.EventId)
        ));

        //Handle success from the operation.
        deleteEventFlowPipeline.push((flowPromise) => flowPromise.then((operationResult) => {
            Assert.StrictAssert(operationResult != null, "Delete operation unexpectedly returned a null result to success handler");
            Assert.StrictAssert(operationResult.result === true, "Delete Operation returned failed result indicator to operation success handler.");

            logSvc.debug("Got event deleted success result.");
            return operationResult;
        }));

        deleteEventFlowPipeline.push(this._addWrapCalendarSvcErrorsFlowStep({
            currentFlowName: deleteFlowName
        }));

        // Success: Notify the user using a success-style modal (because the event editor is now targeting a non-existent event), and then automatically redirect to the calendar.
        deleteEventFlowPipeline.push(this._addActivateLoadingStateFlowStep({
            loadingMessageType: successLoadingMessageType
        }));
        deleteEventFlowPipeline.push(this._addSetLoadingMessageFlowStep(0x1014));
        deleteEventFlowPipeline.push((flowPromise) => flowPromise.then(() => timeoutSvc(() => this.leaveEditor(), defaultSuccessMessageViewTime)));

        // Add error handlers for the expected and unexpected cases.
        deleteEventFlowPipeline.push(this._addFlowErrorHandler());
        deleteEventFlowPipeline.push(this._addCatchAllErrorHandler({
            flowName: deleteFlowName,
            errorMessageText: 0x1A0D
        }));

        logSvc.debug(`Initiating ${deleteFlowName} event editor flow`);
        let deleteEventFlowPromise = this._runFlowPipeline({
            pipelineSteps: deleteEventFlowPipeline
        });

        return deleteEventFlowPromise;
    }
}

TrackingEventEditorController.$inject = [
    "$scope",
    "$sanitize",
    "$http",
    "$timeout",
    "$filter",
    "$document",
    "$window",
    "$anchorScroll",
    "$log",
    "$state",
    "$transition$",
    "$previousState",
    "$uibModal",
    "TimeZone",
    "tbFormatTZNameFilter",
    "AllStatesService",
    "AllTrackingStatuses",
    "AllEventTypesService",
    "TrackingCalendarData",
    "UserInfoService",
    "TrackingEventWorkflowService",
    "TrackingTargetFactory",
    "UserMessages",
    "CalendarGlobalViewService"
];

TrackingEventEditorController.dependencies = DependencyListBuilder([
    EventTrackersEditorDialogController.dependencies,
    EventAttendeesEditorDialogController.dependencies,
    ConfirmDeleteDialogController.dependencies,
    [
        ngSanitize,
        ngAnimate,
        ngAria,
        uiRouter,
        uiRouterPreviousStateModule,
        uiBootstrap,
        kendoDirectives,
        TimeZoneDataServiceModule,
        TimeZoneNameFormatFilterModule,
        AllStatesServiceModule,
        AllTrackingStatusesServiceModule,
        AllEventTypesServiceModule,
        TrackingCalendarDataServiceModule,
        UserInfoServiceModule,
        StatusLabelComponentModule,
        OfficeTypeLabelTypeFilterModule,
        CurrentCandidaciesFilterModule,
        TrackingEventWorkflowServiceModule,
        AlertTypeFilterModule,
        TrackingTargetFactoryModule,
        UserMessagesServiceModule,
        CalendarGlobalViewServiceModule
    ]
]);

TrackingEventEditorController.template = require("./TrackingEventEditor.tpl.html");
TrackingEventEditorController.controllerAs = "eventEditor";

export default TrackingEventEditorController;



//////////////////
// WEBPACK FOOTER
// ./src/views/tracking-event-editor/TrackingEventEditorController.js
// module id = nhJq
// module chunks = 1